import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";
import { DAYS, MONTHS, EVENT_COLORS, emptyEvent } from "./helpers";

/**
 * Calendar — month grid + event modal + selected day panel.
 */
export default function CalendarTab() {
  const [calDate, setCalDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent());

  const load = useCallback(async () => {
    try {
      const y = calDate.getFullYear();
      const m = calDate.getMonth();
      const start = new Date(y, m, 1).toISOString();
      const end = new Date(y, m + 1, 0, 23, 59, 59).toISOString();
      const res = await axios.get(`${API}/crm/calendar/events`, { params: { start, end } });
      setEvents(res.data.items);
    } catch {
      toast.error("Failed to load events");
    }
  }, [calDate]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/crm/calendar/events/${editing.id}`, form);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/crm/calendar/events`, form);
        toast.success("Event created");
      }
      setShowForm(false); setEditing(null); setForm(emptyEvent());
      load();
    } catch {
      toast.error("Failed to save event");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/crm/calendar/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const share = (ev) => {
    const start = new Date(ev.start_time);
    const end = new Date(ev.end_time);
    const text = `${ev.title}\n${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${ev.location ? `\nLocation: ${ev.location}` : ""}${ev.description ? `\n${ev.description}` : ""}`;
    if (navigator.share) navigator.share({ title: ev.title, text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Event details copied to clipboard"); }
  };

  const getCalendarDays = () => {
    const y = calDate.getFullYear();
    const m = calDate.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const eventsForDay = (day) => {
    if (!day) return [];
    const y = calDate.getFullYear();
    const m = calDate.getMonth();
    return events.filter((e) => {
      const d = new Date(e.start_time);
      return d.getFullYear() === y && d.getMonth() === m && d.getDate() === day;
    });
  };

  const today = new Date();
  const isToday = (day) => day && calDate.getFullYear() === today.getFullYear() && calDate.getMonth() === today.getMonth() && day === today.getDate();
  const prevMonth = () => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() - 1, 1));
  const nextMonth = () => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() + 1, 1));

  return (
    <div data-testid="crm-calendar">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={prevMonth} className="text-[#E8E4DC] hover:text-white p-2" data-testid="cal-prev">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h2 className="font-heading text-2xl font-bold text-white" data-testid="cal-month-year">
            {MONTHS[calDate.getMonth()]} {calDate.getFullYear()}
          </h2>
          <button onClick={nextMonth} className="text-[#E8E4DC] hover:text-white p-2" data-testid="cal-next">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
        <button onClick={() => { setEditing(null); setForm(emptyEvent()); setShowForm(true); }} className="btn-primary text-sm" data-testid="add-event-btn">+ New Event</button>
      </div>

      <div className="card-glass overflow-hidden" data-testid="calendar-grid">
        <div className="grid grid-cols-7">
          {DAYS.map((d) => (
            <div key={d} className="p-3 text-center text-[#E8E4DC] text-sm font-medium border-b border-white/10 bg-white/5">{d}</div>
          ))}
          {getCalendarDays().map((day, i) => {
            const dayEvents = eventsForDay(day);
            return (
              <div
                key={i}
                className={`min-h-[100px] p-2 border-b border-r border-white/5 cursor-pointer transition-colors hover:bg-white/5 ${isToday(day) ? "bg-[#A3E635]/5" : ""} ${!day ? "bg-black/20" : ""}`}
                onClick={() => { if (day) setSelectedDay(day); }}
                data-testid={day ? `cal-day-${day}` : undefined}
              >
                {day && (
                  <>
                    <span className={`text-sm font-medium ${isToday(day) ? "text-[#A3E635] font-bold" : "text-[#E8E4DC]"}`}>{day}</span>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map((e) => (
                        <div
                          key={e.id}
                          className="text-xs px-1.5 py-0.5 rounded truncate cursor-pointer"
                          style={{ backgroundColor: `${e.color}20`, color: e.color, borderLeft: `2px solid ${e.color}` }}
                          onClick={(ev) => { ev.stopPropagation(); setEditing(e); setForm(e); setShowForm(true); }}
                          data-testid={`cal-event-${e.id}`}
                        >
                          {e.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <span className="text-xs text-[#E8E4DC]">+{dayEvents.length - 3} more</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="mt-6 card-glass p-6" data-testid="selected-day-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">{MONTHS[calDate.getMonth()]} {selectedDay}, {calDate.getFullYear()}</h3>
            <button onClick={() => setSelectedDay(null)} className="text-[#E8E4DC] hover:text-white">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {eventsForDay(selectedDay).length === 0 ? (
            <p className="text-[#E8E4DC] text-sm">No events this day</p>
          ) : (
            <div className="space-y-3">
              {eventsForDay(selectedDay).map((e) => (
                <div key={e.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-1 h-full min-h-[40px] rounded-full shrink-0" style={{ backgroundColor: e.color }}></div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{e.title}</p>
                    <p className="text-[#E8E4DC] text-sm">
                      {new Date(e.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - {new Date(e.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    {e.location && <p className="text-[#E8E4DC] text-sm mt-1">{e.location}</p>}
                    {e.description && <p className="text-[#E8E4DC] text-sm mt-1">{e.description}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => share(e)} className="text-[#E8E4DC] hover:text-[#A3E635] p-1" title="Share" data-testid={`share-event-${e.id}`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                    </button>
                    <button onClick={() => { setEditing(e); setForm(e); setShowForm(true); }} className="text-[#E8E4DC] hover:text-[#A3E635] p-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => remove(e.id)} className="text-[#E8E4DC] hover:text-red-400 p-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" data-testid="event-form-modal">
          <div className="card-glass p-6 w-full max-w-lg" style={{ background: "rgba(0,0,0,0.95)" }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-white">{editing ? "Edit Event" : "New Event"}</h2>
              <div className="flex items-center gap-2">
                {editing && (
                  <button onClick={() => share(form)} className="text-[#E8E4DC] hover:text-[#A3E635] transition-colors p-1" title="Share Event" data-testid="ef-share">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  </button>
                )}
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-[#E8E4DC] hover:text-white">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark w-full" placeholder="Event Title *" data-testid="ef-title" />
              <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark w-full h-20 resize-none" placeholder="Description" data-testid="ef-desc" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#E8E4DC] text-xs mb-1 block">Start</label>
                  <input type="datetime-local" value={form.start_time?.slice(0, 16)} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input-dark w-full" style={{ fontSize: "13px", minWidth: 0 }} data-testid="ef-start" />
                </div>
                <div>
                  <label className="text-[#E8E4DC] text-xs mb-1 block">End</label>
                  <input type="datetime-local" value={form.end_time?.slice(0, 16)} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input-dark w-full" style={{ fontSize: "13px", minWidth: 0 }} data-testid="ef-end" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-dark flex-1" placeholder="Location" data-testid="ef-location" />
                {form.location && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.location)}`} target="_blank" rel="noopener noreferrer" className="shrink-0 p-2.5 rounded-lg bg-white/5 border border-white/10 text-[#E8E4DC] hover:text-[#A3E635] hover:border-[#A3E635]/30 transition-colors" title="View on Google Maps" data-testid="ef-maps-link">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </a>
                )}
              </div>
              <div>
                <label className="text-[#E8E4DC] text-xs mb-1 block">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {EVENT_COLORS.map((c) => (
                    <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full transition-all ${form.color === c ? "ring-2 ring-white scale-110" : "opacity-60 hover:opacity-100"}`} style={{ backgroundColor: c }} data-testid={`ef-color-${c}`} />
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[#E8E4DC] text-sm cursor-pointer">
                <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} className="accent-[#A3E635]" />
                All Day Event
              </label>
            </div>
            <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary flex-1" data-testid="ef-cancel">Cancel</button>
              {editing && (
                <button onClick={() => share(form)} className="btn-secondary flex-1" data-testid="ef-share-btn">
                  <svg className="w-4 h-4 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  Share
                </button>
              )}
              <button onClick={save} className="btn-primary flex-1" data-testid="ef-save">{editing ? "Update Event" : "Create Event"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
